import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import './PaymentStripe.css';

/**
 * PayPal grįžimo puslapis: po sėkmingo mokėjimo PayPal nukreipia čia su token ir order_id.
 * Patvirtiname mokėjimą per /payment/verify ir nukreipiame į užsakymo puslapį.
 */
function PaymentPayPalReturn() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying');

  const invalidateCart = () => {
    queryClient.invalidateQueries({ queryKey: ['cart'] });
  };

  const verifyPaymentMutation = useMutation({
    mutationFn: async () => {
      if (!orderId) throw new Error('Order ID is required');
      if (!token) throw new Error('PayPal token is required');
      const authToken = localStorage.getItem('token');
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const res = await apiClient.post('/payment/verify', {
        order_id: parseInt(orderId, 10),
        payment_method: 'paypal',
        session_id: token,
      }, { headers });
      return res.data;
    },
    onSuccess: (data) => {
      const successStatuses = ['succeeded', 'paid', 'complete', 'COMPLETED'];
      const statusVal = data.status && (typeof data.status === 'string' ? data.status.toUpperCase() : data.status);
      const isSuccess = successStatuses.includes(statusVal) || data.is_paid === true;
      if (isSuccess) {
        invalidateCart();
        setStatus('success');
        setTimeout(() => navigate(`/orders/${orderId}`), 2500);
      } else {
        setStatus('error');
      }
    },
    onError: () => setStatus('error'),
  });

  useEffect(() => {
    if (orderId && token) {
      verifyPaymentMutation.mutate();
      return;
    }
    if (!orderId || !token) {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, token]);

  if (status === 'verifying') {
    return (
      <div className="payment-confirm-page">
        <div className="payment-confirm-container">
          <div className="payment-confirm-header">
            <h1>{t('paymentVerifying')}</h1>
            <p className="payment-confirm-subtitle">{t('paymentVerifyingHint')}</p>
          </div>
          <div className="payment-confirm-card payment-confirm-verifying">
            <div className="payment-confirm-loading" aria-hidden="true" />
            <p className="payment-confirm-loading-text">{t('paymentVerifyingHint')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="payment-confirm-page">
        <div className="payment-confirm-container">
          <div className="payment-confirm-header">
            <h1>{t('paymentSuccessTitle')}</h1>
            <p className="payment-confirm-subtitle">{t('paymentSuccessText')}</p>
          </div>
          <div className="payment-confirm-card payment-confirm-success">
            <div className="payment-confirm-icon payment-confirm-icon-success" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <p className="payment-confirm-message">{t('paymentSuccessText')}</p>
            {orderId && (
              <p className="payment-confirm-redirect">
                {t('redirectingToOrder') || 'Peradresuojame į užsakymą...'}
              </p>
            )}
            <div className="payment-confirm-actions">
              {orderId && (
                <button type="button" onClick={() => navigate(`/orders/${orderId}`)} className="btn btn-primary">
                  {t('viewOrder')}
                </button>
              )}
              <Link to="/orders" className="btn btn-secondary">
                {t('backToOrders') || 'Mano užsakymai'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-confirm-page">
      <div className="payment-confirm-container">
        <div className="payment-confirm-header">
          <h1>{t('paymentConfirmFailed')}</h1>
          <p className="payment-confirm-subtitle">{t('paymentConfirmErrorText')}</p>
        </div>
        <div className="payment-confirm-card payment-confirm-error">
          <div className="payment-confirm-icon payment-confirm-icon-error" aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
          </div>
          <p className="payment-confirm-message">{t('paymentConfirmErrorText')}</p>
          <p className="payment-confirm-hint">{t('checkConsoleHint')}</p>
          <div className="payment-confirm-actions">
            {orderId && (
              <button type="button" onClick={() => navigate(`/orders/${orderId}`)} className="btn btn-primary">
                {t('viewOrder')}
              </button>
            )}
            <button type="button" onClick={() => navigate('/orders')} className="btn btn-secondary">
              {t('backToOrders') || 'Mano užsakymai'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentPayPalReturn;
